import { useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import {
  type ActionDashboardItem,
  type TaskDashboardItem,
  useCollection,
  useGetActionsDashboard,
  useGetTasksDashboard,
} from '@frontend/api';
import {
  Box,
  Button,
  Center,
  ChevronDownIcon,
  Empty,
  EmptyDescription,
  EmptyDrawerIcon,
  EmptyHeadline,
  Flex,
  Pagination,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Spinner,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  TabQuantityBadge,
  Tabs,
  Text,
  UserGroupBadge,
  VStack,
  useDisclosure,
} from '@frontend/components';

import {
  DefaultTableShadowOverlay,
  type StickyElementRefs,
} from 'components/listTablePage';

import { TASK_STATUSES } from 'modules/tasks/data/constants';

import {
  getAvailableGroups,
  getUserGroupCollectionId,
  getUserId,
} from 'store/session/selectors';

import { useUserGroupCollectionContext } from 'utils/multisite/UserGroupCollectionProvider';
import { useVisibleUserGroups } from 'utils/multisite/hooks/useVisibleUserGroups';
import { paginationStatus } from 'utils/stringUtils';

import { ReqsReviewAction } from '../components/ReqsReviewAction';
import { TaskDashboardAction } from '../components/TaskDashboardAction';
import {
  Pane,
  PaneBody,
  PaneHeader,
  PaneTitle,
  PaneFooter,
} from '../components/pane';
import { MULTISITE_DASHBOARD_POLL_QUERY_OPTIONS } from '../multisiteDashboardDataRefresh';

const PAGE_SIZE = 20;
const VIEW_ALL_TASKS_URL = `/tasks?status_in=${TASK_STATUSES.OPEN},${TASK_STATUSES.OVERDUE}`;

// No multisite websocket topics exist for tasks or review items yet.
const websocketOptions = { topic: null } as const;

const EMPTY_STICKY_REFS: StickyElementRefs = {};

const formatDisjunction = (names: string[]): string => {
  if (names.length === 0) return '';
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} or ${names[1]}`;
  return `${names.slice(0, -1).join(', ')}, or ${names.at(-1)}`;
};

type ViewAllTasksButtonProps = {
  userId: number | null;
};

const ViewAllTasksButton = ({ userId }: ViewAllTasksButtonProps) => {
  const { isOpen, onClose, onToggle } = useDisclosure();
  const { initChangeSite } = useUserGroupCollectionContext();
  const visibleGroups = useVisibleUserGroups();

  const sortedGroups = useMemo(
    () =>
      [...visibleGroups].sort((a, b) =>
        a.businessName.localeCompare(b.businessName),
      ),
    [visibleGroups],
  );

  if (visibleGroups.length <= 1) {
    return (
      <Button
        variant="link"
        size="md"
        as="a"
        href={`/tasks?assigned_to_in=${userId ?? ''}&status_in=${
          TASK_STATUSES.OPEN
        },${TASK_STATUSES.OVERDUE}`}
      >
        View All Tasks
      </Button>
    );
  }

  return (
    <Popover isOpen={isOpen} onClose={onClose} placement="top">
      <PopoverTrigger>
        <Button
          variant="link"
          size="md"
          icon={<ChevronDownIcon />}
          onClick={onToggle}
        >
          View All Tasks
        </Button>
      </PopoverTrigger>
      <Portal>
        <PopoverContent width="max-content">
          <PopoverBody px={0}>
            <Text
              fontSize="sm"
              fontWeight="heavy"
              color="feature.secondary"
              px={2}
            >
              FOR SITE:
            </Text>
            <VStack align="stretch" spacing={0}>
              {sortedGroups.map((group) => (
                <Box
                  key={group.id}
                  as="button"
                  type="button"
                  display="block"
                  textAlign="left"
                  bg="transparent"
                  border="none"
                  px={2}
                  py={0.5}
                  cursor="pointer"
                  _hover={{ bg: 'interaction.surfaceHover' }}
                  onClick={() => {
                    onClose();
                    initChangeSite({
                      nextUrl: VIEW_ALL_TASKS_URL,
                      userGroupId: group.id,
                    });
                  }}
                >
                  <UserGroupBadge
                    name={group.businessName}
                    icon={group.icon}
                    colorPalette={group.color}
                    size="sm"
                  />
                </Box>
              ))}
            </VStack>
          </PopoverBody>
        </PopoverContent>
      </Portal>
    </Popover>
  );
};

type TabContentProps = {
  totalCount: number;
  page: number;
  onPageChange: (page: number) => void;
  isLoading: boolean;
  filteredSiteLabel: string | null;
};

type ReviewItemsContentProps = TabContentProps & {
  items: ActionDashboardItem[];
};

const ReviewItemsContent = ({
  items,
  totalCount,
  page,
  onPageChange,
  isLoading,
  filteredSiteLabel,
}: ReviewItemsContentProps) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const showPagination = totalCount > PAGE_SIZE;
  const isEmpty = !isLoading && items.length === 0;

  return (
    <>
      <Flex position="relative" flex={1} flexDirection="column" minHeight={0}>
        {isLoading ? (
          <Center p={2} flex={1} bg={'page.background'} justifyContent="center">
            <Spinner size="xl" />
          </Center>
        ) : isEmpty ? (
          <Empty p={2} flex={1} bg={'page.background'} justifyContent="center">
            <EmptyDrawerIcon />
            <EmptyHeadline>No review items</EmptyHeadline>
            <EmptyDescription>
              {filteredSiteLabel
                ? `You don't have any unresolved review items for ${filteredSiteLabel}`
                : "You don't have any unresolved review items assigned to you"}
            </EmptyDescription>
          </Empty>
        ) : (
          <>
            <DefaultTableShadowOverlay
              overflowBoxRef={scrollRef}
              stickyElementRefs={EMPTY_STICKY_REFS}
            />
            <VStack
              ref={scrollRef}
              flex={1}
              minHeight={0}
              overflowY="auto"
              spacing={0}
              align="stretch"
            >
              {items.map((item) => (
                <ReqsReviewAction key={item.uuid} item={item} />
              ))}
            </VStack>
          </>
        )}
      </Flex>

      {showPagination && (
        <PaneFooter justifyContent="flex-end">
          <Box flex={1}>
            <Text>{paginationStatus(page, PAGE_SIZE, totalCount, 'item')}</Text>
          </Box>
          <Pagination
            totalItems={totalCount}
            itemsPerPage={PAGE_SIZE}
            currentPage={page}
            onChangePage={onPageChange}
          />
        </PaneFooter>
      )}
    </>
  );
};

type TasksContentProps = TabContentProps & {
  items: TaskDashboardItem[];
  userId: number | null;
};

const TasksContent = ({
  items,
  totalCount,
  page,
  onPageChange,
  userId,
  isLoading,
  filteredSiteLabel,
}: TasksContentProps) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const showPagination = totalCount > PAGE_SIZE;
  const isEmpty = !isLoading && items.length === 0;

  return (
    <>
      <Flex position="relative" flex={1} flexDirection="column" minHeight={0}>
        {isLoading ? (
          <Center p={2}>
            <Spinner size="xl" />
          </Center>
        ) : isEmpty ? (
          <Empty p={2} flex={1} bg={'page.background'} justifyContent="center">
            <EmptyDrawerIcon />
            <EmptyHeadline>No tasks</EmptyHeadline>
            <EmptyDescription>
              {filteredSiteLabel
                ? `You don't have any open tasks for ${filteredSiteLabel}`
                : "You don't have any open tasks assigned to you"}
            </EmptyDescription>
          </Empty>
        ) : (
          <>
            <DefaultTableShadowOverlay
              overflowBoxRef={scrollRef}
              stickyElementRefs={EMPTY_STICKY_REFS}
            />
            <VStack
              ref={scrollRef}
              flex={1}
              minHeight={0}
              overflowY="auto"
              spacing={0}
              align="stretch"
            >
              {items.map((task) => (
                <TaskDashboardAction key={task.id} task={task} />
              ))}
            </VStack>
          </>
        )}
      </Flex>

      <PaneFooter
        justifyContent={showPagination ? undefined : 'center'}
        alignItems="center"
      >
        {showPagination ? (
          <>
            <Box flex={1}>
              <Text>
                {paginationStatus(page, PAGE_SIZE, totalCount, 'task')}
              </Text>
            </Box>
            <Pagination
              totalItems={totalCount}
              itemsPerPage={PAGE_SIZE}
              currentPage={page}
              onChangePage={onPageChange}
            />
            <Box flex={1} display="flex" justifyContent="flex-end">
              <ViewAllTasksButton userId={userId} />
            </Box>
          </>
        ) : (
          <ViewAllTasksButton userId={userId} />
        )}
      </PaneFooter>
    </>
  );
};

type AssignedToYouContentProps = {
  activeGroupIds: number[];
};

const AssignedToYouContent = ({
  activeGroupIds,
}: AssignedToYouContentProps) => {
  const userId = useSelector(getUserId);
  const availableGroups = useSelector(getAvailableGroups);
  const collectionId = useSelector(getUserGroupCollectionId);

  const [reviewItemsPage, setReviewItemsPage] = useState(1);
  const [tasksPage, setTasksPage] = useState(1);

  const groups = activeGroupIds.length > 0 ? activeGroupIds : undefined;

  const { data: collection } = useCollection(
    { collectionId },
    { queryOptions: { enabled: !!collectionId } },
  );

  const filteredSiteLabel = useMemo(() => {
    if (
      activeGroupIds.length >= availableGroups.length ||
      !collection?.userGroups
    ) {
      return null;
    }
    const activeSet = new Set(activeGroupIds);
    const names = collection.userGroups
      .filter((g) => activeSet.has(g.id))
      .map((g) => g.businessName)
      .toSorted();
    return formatDisjunction(names);
  }, [activeGroupIds, availableGroups.length, collection]);

  const {
    data: reviewItemsData,
    isLoading: isLoadingReviewItems,
    isFetching: isFetchingReviewItems,
  } = useGetActionsDashboard(
    { page: reviewItemsPage, pageSize: PAGE_SIZE, groups },
    {
      queryOptions: {
        useErrorBoundary: true,
        keepPreviousData: true,
        ...MULTISITE_DASHBOARD_POLL_QUERY_OPTIONS,
      },
    },
  );

  const {
    data: tasksData,
    isLoading: isLoadingTasks,
    isFetching: isFetchingTasks,
  } = useGetTasksDashboard(
    {
      page: tasksPage,
      pageSize: PAGE_SIZE,
      assignedToIn: userId ? [userId] : undefined,
      statusIn: [TASK_STATUSES.OPEN, TASK_STATUSES.OVERDUE],
      groups,
    },
    {
      queryOptions: {
        useErrorBoundary: true,
        keepPreviousData: true,
        ...MULTISITE_DASHBOARD_POLL_QUERY_OPTIONS,
      },
    },
  );

  if (isLoadingReviewItems || isLoadingTasks) {
    return (
      <Center height="full">
        <Spinner size="xl" />
      </Center>
    );
  }

  const reviewCount = reviewItemsData?.count ?? 0;
  const taskCount = tasksData?.count ?? 0;
  const isFetching = isFetchingReviewItems || isFetchingTasks;

  return (
    <>
      <TabList>
        <Tab>
          Review Items <TabQuantityBadge>{reviewCount}</TabQuantityBadge>
        </Tab>
        <Tab>
          Tasks <TabQuantityBadge>{taskCount}</TabQuantityBadge>
        </Tab>
      </TabList>

      <TabPanels
        display="flex"
        flex={1}
        flexDirection="column"
        alignItems="stretch"
        minHeight={0}
      >
        <TabPanel
          display="flex"
          flexDirection="column"
          position="relative"
          maxWidth="100%"
          flex={1}
          minHeight={0}
          p={0}
        >
          <ReviewItemsContent
            items={reviewItemsData?.results ?? []}
            totalCount={reviewCount}
            page={reviewItemsPage}
            onPageChange={setReviewItemsPage}
            isLoading={isLoadingReviewItems}
            filteredSiteLabel={filteredSiteLabel}
          />
        </TabPanel>

        <TabPanel
          display="flex"
          flexDirection="column"
          position="relative"
          maxWidth="100%"
          flex={1}
          minHeight={0}
          p={0}
        >
          <TasksContent
            items={tasksData?.results ?? []}
            totalCount={taskCount}
            page={tasksPage}
            onPageChange={setTasksPage}
            userId={userId}
            isLoading={isLoadingTasks}
            filteredSiteLabel={filteredSiteLabel}
          />
        </TabPanel>
      </TabPanels>
      {isFetching && (
        <Center
          as="span"
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          zIndex="overlay"
          bg="page.background"
          opacity={0.5}
        >
          <Spinner size="xl" />
        </Center>
      )}
    </>
  );
};

export type AssignedToYouPaneProps = {
  activeGroupIds: number[];
};

export const AssignedToYouPane = ({
  activeGroupIds,
}: AssignedToYouPaneProps) => (
  <Pane refetchQueryKey="" websocketOptions={websocketOptions}>
    <PaneHeader>
      <PaneTitle>Assigned to You</PaneTitle>
    </PaneHeader>
    <Tabs
      as={PaneBody}
      isFitted
      display="flex"
      flexDirection="column"
      minHeight={0}
      position="relative"
    >
      <AssignedToYouContent activeGroupIds={activeGroupIds} />
    </Tabs>
  </Pane>
);
